import type { CustomFieldDTO } from '@tempo-io/item-store-dto';
import { customFieldSchema } from '@tempo-io/item-store-dto';
import { z } from 'zod';
import { type TimelineLayoutDTO, timelineLayoutDTOSchema } from './layout-dto';
import {
  type IdentifiableTItem,
  type ItemDetails,
  identifiableTItemSchema,
  itemDetailsSchema,
  type LegendItem,
  legendItemSchema,
  type PublishedMetadata,
  publishedMetadataSchema,
} from './published-swimlane-dto';

export type { IdentifiableTItem, ItemDetails, LegendItem, PublishedMetadata };

export interface PublishedItemType {
  id: number;
  name: string;
  icon?: string;
  color?: string;
  itemLevelId: number;
}

export interface PublishedItemLevel {
  id: number;
  level: number;
  name: string;
}

export interface PublishedItemStatus {
  id: number;
  name: string;
  type: string;
  color: string;
}

export interface PublishedTeam {
  id: number;
  name: string;
  color: string;
}

export interface HeaderConfig {
  orderedHeaderIds: string[];
  hiddenHeaderIds: string[];
}

export interface PublishedTimelineResponse {
  items: IdentifiableTItem[];
  itemTypes: PublishedItemType[];
  itemLevels: PublishedItemLevel[];
  itemStatuses: PublishedItemStatus[];
  teams: PublishedTeam[];
  mainHeaderConfig: HeaderConfig;
  subHeaderConfigs: Record<string, HeaderConfig>;
  layout: TimelineLayoutDTO;
  metadata: PublishedMetadata;
  itemDetails: Record<string, ItemDetails>;
  customFieldDefinitions: CustomFieldDTO[];
  hiddenFieldIds?: string[];
  legendItems?: LegendItem[];
}

export const publishedItemTypeSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  icon: z.string().optional(),
  color: z.string().optional(),
  itemLevelId: z.number().int(),
});

export const publishedItemLevelSchema = z.object({
  id: z.number().int(),
  level: z.number().int(),
  name: z.string(),
});

export const publishedItemStatusSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  type: z.string(),
  color: z.string(),
});

export const headerConfigSchema = z.object({
  orderedHeaderIds: z.array(z.string()),
  hiddenHeaderIds: z.array(z.string()),
});

export const publishedTeamSchema = z.object({
  id: z.number(),
  name: z.string(),
  color: z.string(),
});

export const publishedTimelineResponseSchema = z.object({
  items: z.array(identifiableTItemSchema),
  itemTypes: z.array(publishedItemTypeSchema),
  itemLevels: z.array(publishedItemLevelSchema),
  itemStatuses: z.array(publishedItemStatusSchema),
  teams: z.array(publishedTeamSchema),
  mainHeaderConfig: headerConfigSchema,
  subHeaderConfigs: z.record(z.string(), headerConfigSchema),
  layout: timelineLayoutDTOSchema,
  metadata: publishedMetadataSchema,
  itemDetails: z.record(z.string(), itemDetailsSchema),
  customFieldDefinitions: z.array(customFieldSchema).default([]),
  hiddenFieldIds: z.array(z.string()).optional(),
  legendItems: z.array(legendItemSchema).optional(),
});
